import { ResourceClient } from '../client/types';
import { FunctionalState } from '../resolvers/definition/resolver';
import { ResolverState } from '../resolvers/definition/resolverState';
import { AnyResourceDefinition, ResourceDefinition } from '../resources/types';
import { MutationDefinition, MutationOverrides } from './types';

type MutationClaim = {
  resource: { identifyInstance: (params: any) => string; typeName: string };
  params: unknown;
  instanceKey: string;
};

type OptimisticEntry = {
  origin: ResolverState<any, any> | undefined;
  optimisticStates: Set<ResolverState<any, any> | undefined>;
  count: number;
};

// Entries are cleaned up when mutations resolve or reject. If a mutation's
// promise never settles, its entries will leak.
const optimisticRegistry = new Map<string, OptimisticEntry>();

export const __testOnlyResetOptimisticRegistry = () => {
  if (process.env.NODE_ENV !== 'production') {
    optimisticRegistry.clear();
  }
};

const resolveState = (
  state: FunctionalState<any, any>,
  current: ResolverState<any, any> | undefined
): ResolverState<any, any> | undefined => {
  if (typeof state === 'function') {
    return state(current) ?? undefined;
  }
  return state;
};

const releaseRegistryClaims = (claims: Map<string, MutationClaim>) => {
  for (const claim of claims.values()) {
    const entry = optimisticRegistry.get(claim.instanceKey);
    if (entry) {
      entry.count--;
      if (entry.count === 0) {
        optimisticRegistry.delete(claim.instanceKey);
      }
    }
  }
};

export const executeMutation = <TParams, TResult, TError extends Error = Error>(
  definition: MutationDefinition<TParams, TResult, TError>,
  params: TParams,
  client: ResourceClient,
  overrides?: MutationOverrides<TParams, TResult, TError>
): Promise<TResult> => {
  const claims = new Map<string, MutationClaim>();

  const getState = (args: {
    resource: ResourceDefinition<any, any>;
    params?: any;
  }) => client.getState({ resource: args.resource, params: args.params });

  const optimisticSetState = (args: {
    resource: ResourceDefinition<any, any>;
    params?: any;
    state: FunctionalState<any, any>;
  }) => {
    const instanceKey = args.resource.identifyInstance(args.params);

    const currentState = client.getState({
      resource: args.resource,
      params: args.params,
      instanceKey,
    });

    if (!claims.has(instanceKey)) {
      claims.set(instanceKey, {
        resource: args.resource,
        params: args.params,
        instanceKey,
      });

      if (!optimisticRegistry.has(instanceKey)) {
        optimisticRegistry.set(instanceKey, {
          origin: currentState,
          optimisticStates: new Set(),
          count: 0,
        });
      }
      optimisticRegistry.get(instanceKey)!.count++;
    }

    const concreteState = resolveState(args.state, currentState);
    optimisticRegistry.get(instanceKey)!.optimisticStates.add(concreteState);

    client.setState({
      resource: args.resource,
      params: args.params,
      instanceKey,
      state: concreteState,
      optimistic: true,
    });
  };

  const directSetState = (args: {
    resource: ResourceDefinition<any, any>;
    params?: any;
    state: FunctionalState<any, any>;
  }) => {
    client.setState({
      resource: args.resource,
      params: args.params,
      state: args.state,
    });
  };

  const invalidate = (args: {
    resource: ResourceDefinition<any, any>;
    params?: any;
  }) => {
    client.invalidate({ resource: args.resource, params: args.params });
  };

  const invalidateAll = (args: { resource: AnyResourceDefinition }) => {
    client.invalidateAll({ resource: args.resource });
  };

  const optimisticCtx = { params, getState, setState: optimisticSetState };

  if (definition.optimistic) {
    definition.optimistic(optimisticCtx);
  }
  if (overrides?.optimistic) {
    overrides.optimistic(optimisticCtx);
  }

  return definition.mutation(params).then(
    async (result) => {
      releaseRegistryClaims(claims);
      claims.clear();

      const resolvedCtx = {
        params,
        result,
        getState,
        setState: directSetState,
        invalidate,
        invalidateAll,
      };

      await definition.resolved?.(resolvedCtx);
      await overrides?.resolved?.(resolvedCtx);

      return result;
    },
    async (error: TError) => {
      for (const claim of claims.values()) {
        const entry = optimisticRegistry.get(claim.instanceKey);
        if (!entry) {
          continue;
        }

        entry.count--;

        if (entry.count > 0) {
          continue;
        }

        const currentState = client.getState({
          resource: claim.resource as any,
          params: claim.params,
          instanceKey: claim.instanceKey,
        });

        // Known edge case: if two mutations (A, B) both optimistically update
        // the same resource and A rejects before B settles, A's rollback will
        // be skipped here (entry.count > 0, since B still holds a claim). If B
        // then also rejects, it will roll back to A's optimistic state rather
        // than the true pre-A origin. This scenario is vanishingly rare in
        // practice. Fully solving it would require baking an optimistic layer
        // into the client core, which trades simplicity for correctness in an
        // edge case that consumers can avoid by not overlapping mutations on the
        // same resource.
        if (entry.optimisticStates.has(currentState)) {
          client.setState({
            resource: claim.resource as any,
            params: claim.params,
            instanceKey: claim.instanceKey,
            state: entry.origin,
          });
        }

        optimisticRegistry.delete(claim.instanceKey);
      }

      const rejectedCtx = {
        params,
        error,
        getState,
        setState: directSetState,
        invalidate,
        invalidateAll,
      };

      if (definition.rejected) {
        try {
          await definition.rejected(rejectedCtx);
        } catch (callbackError) {
          if (process.env.NODE_ENV !== 'production') {
            console.error(
              '[Pulse] rejected callback threw an error:',
              callbackError
            );
          }
        }
      }

      if (overrides?.rejected) {
        try {
          await overrides.rejected(rejectedCtx);
        } catch (callbackError) {
          if (process.env.NODE_ENV !== 'production') {
            console.error(
              '[Pulse] rejected override threw an error:',
              callbackError
            );
          }
        }
      }

      throw error;
    }
  );
};
